import React, { createContext, useContext, useState } from 'react'
import FlagGroup from '@atlaskit/flag/flag-group'
import { AutoDismissFlag } from '@atlaskit/flag'
import ErrorIcon from '@atlaskit/icon/glyph/error'
import SuccessIcon from '@atlaskit/icon/glyph/check-circle'
import WarningIcon from '@atlaskit/icon/glyph/warning'
import InfoIcon from '@atlaskit/icon/glyph/info'

const NotificationContext = createContext()

/**
 * NOTIFICATION PROVIDER
 *
 * Provides Jira-style flag notifications throughout the app.
 * Supports error, success, warning, and info notifications.
 */
export const NotificationProvider = ({ children }) => {
  const [flags, setFlags] = useState([])

  /**
   * ADD FLAG NOTIFICATION
   * Internal helper to add a new flag
   */
  const addFlag = (title, message, appearance, icon) => {
    const id = Date.now()
    const newFlag = {
      id,
      title,
      description: message,
      icon,
      appearance
    }
    setFlags(prev => [...prev, newFlag])
  }

  /**
   * SHOW ERROR NOTIFICATION
   * Red flag for errors and failures
   *
   * @param {string} title - Error title (e.g., "Update Failed")
   * @param {string} message - Error message from API response
   */
  const showError = (title, message) => {
    addFlag(
      title,
      message,
      'error',
      <ErrorIcon label="Error" primaryColor="#DE350B" />
    )
  }

  /**
   * SHOW SUCCESS NOTIFICATION
   * Green flag for successful operations
   *
   * @param {string} title - Success title (e.g., "Folder Created")
   * @param {string} message - Success message
   */
  const showSuccess = (title, message) => {
    addFlag(
      title,
      message,
      'success',
      <SuccessIcon label="Success" primaryColor="#00875A" />
    )
  }

  /**
   * SHOW WARNING NOTIFICATION
   * Yellow flag for warnings
   *
   * @param {string} title - Warning title
   * @param {string} message - Warning message
   */
  const showWarning = (title, message) => {
    addFlag(
      title,
      message,
      'warning',
      <WarningIcon label="Warning" primaryColor="#FF8B00" />
    )
  }

  /**
   * SHOW INFO NOTIFICATION
   * Blue flag for informational messages
   *
   * @param {string} title - Info title
   * @param {string} message - Info message
   */
  const showInfo = (title, message) => {
    addFlag(
      title,
      message,
      'info',
      <InfoIcon label="Info" primaryColor="#0065FF" />
    )
  }

  /**
   * DISMISS NOTIFICATION
   * Called automatically after 8s or when user clicks X
   */
  const dismissFlag = (id) => {
    setFlags(prev => prev.filter(flag => flag.id !== id))
  }

  return (
    <NotificationContext.Provider value={{ showError, showSuccess, showWarning, showInfo }}>
      {children}

      {/* Notification container - bottom-left like Jira */}
      <FlagGroup onDismissed={dismissFlag}>
        {flags.map(flag => (
          <AutoDismissFlag
            key={flag.id}
            id={flag.id}
            icon={flag.icon}
            title={flag.title}
            description={flag.description}
            appearance="normal"
            autoDismissSeconds={8}
          />
        ))}
      </FlagGroup>
    </NotificationContext.Provider>
  )
}

/**
 * HOOK TO USE NOTIFICATIONS
 * Usage: const { showError, showSuccess, showWarning, showInfo } = useNotification()
 */
export const useNotification = () => {
  const context = useContext(NotificationContext)
  if (!context) {
    throw new Error('useNotification must be used within NotificationProvider')
  }
  return context
}
